import {Dispatch, SetStateAction, useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Configuration,
    ConsentRequestApi,
    DeviceApi,
    EditionApi,
    Middleware,
    OrderApi,
    PartnerApi,
    ProductApi,
    ResponseContext,
    RoleApi,
    SupportApi,
    TenantApi,
    UserPermissionsApi,
    UserPreferencesApi,
} from '@br/idm-api';
import {useNotifications} from '@br/mg-components';

import {getCsrfToken} from '../../util/csrf';

import {ErrorResponse} from './types';

const useApis = (setError: Dispatch<SetStateAction<ErrorResponse | null>>) => {
    const {t} = useTranslation(['Notifications']);
    const defaultErrorMessage = useMemo(() => t('Notifications:unknownServerError', 'Unknown server error'), [t]);

    const {addNotification, removeNotification} = useNotifications();

    const ErrorHandlingMiddleware: Middleware = useMemo(
        () => ({
            async post(context: ResponseContext): Promise<Response | undefined> {
                const response = context.response;
                if (response.status === 500) {
                    try {
                        // Before calling `json()`, make a new clone of the response.
                        // This avoids `bodyUsed` being set and therefore blocking
                        // future clones of the response (necessary for other
                        // middleware to be called). See `BaseAPI.fetchApi` for where
                        // the cloning happens in the openapi-generator code.
                        setError((await response.clone().json()) as ErrorResponse);
                    } catch {
                        setError({message: defaultErrorMessage});
                    }
                } else if (response.status === 0 || response.status > 500) {
                    setError({message: defaultErrorMessage});
                }
                return response;
            },
        }),
        [defaultErrorMessage, setError]
    );

    // Pull the number of pending partner requests from the header and store it
    const PendingPartnerRequestsMiddleware: Middleware = useMemo(
        () => ({
            // eslint-disable-next-line @typescript-eslint/require-await
            async post(context: ResponseContext): Promise<Response | undefined> {
                const response = context.response;
                const pendingPartnerRequests = response.headers.get('Portal-Pending-Partner-Requests');
                const pendingPartnerRequestId = 'pending-partner-requests';
                if (pendingPartnerRequests !== null) {
                    const count = parseInt(pendingPartnerRequests, 10);
                    if (count > 0) {
                        addNotification({
                            id: pendingPartnerRequestId,
                            title: t('Notifications:pendingPartnerRequests:title', 'Pending partner request', {count}),
                            content: t(
                                'Notifications:pendingPartnerRequests:content',
                                'A partner would like to access some information'
                            ),
                            type: 'informative',
                            // While it's not true that it's read, hide the red dot that
                            // appears as it contributes to a poor visual layout.
                            read: true,
                            // eslint-disable-next-line i18next/no-literal-string
                            href: '/portal/consent-requests/',
                        });
                    } else {
                        removeNotification(pendingPartnerRequestId);
                    }
                }
                return response;
            },
        }),
        [addNotification, removeNotification, t]
    );

    const ErrorHandlingFetch: typeof fetch = useCallback(
        async (input, requestInfo) => {
            try {
                return await fetch(input, requestInfo);
            } catch (e) {
                setError({message: defaultErrorMessage});
                throw e;
            }
        },
        [defaultErrorMessage, setError]
    );

    const configuration = useMemo(
        () =>
            new Configuration({
                basePath: window.location.origin,
                headers: {
                    'X-CSRFToken': getCsrfToken(),
                },
                middleware: [ErrorHandlingMiddleware, PendingPartnerRequestsMiddleware],
                fetchApi: ErrorHandlingFetch,
            }),
        [ErrorHandlingMiddleware, ErrorHandlingFetch, PendingPartnerRequestsMiddleware]
    );

    return useMemo(
        () => ({
            productApi: new ProductApi(configuration),
            editionApi: new EditionApi(configuration),
            roleApi: new RoleApi(configuration),
            partnerApi: new PartnerApi(configuration),
            tenantApi: new TenantApi(configuration),
            supportApi: new SupportApi(configuration),
            orderApi: new OrderApi(configuration),
            deviceApi: new DeviceApi(configuration),
            userPreferencesApi: new UserPreferencesApi(configuration),
            userPermissionsApi: new UserPermissionsApi(configuration),
            consentRequestApi: new ConsentRequestApi(configuration),
        }),
        [configuration]
    );
};

export default useApis;
